import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  IconButton,
  Input,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ImmutableArray } from "@hookstate/core";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CaretLeftIcon } from "../icons/CaretLeftIcon";
import { DragGripIcon } from "../icons/DragGripIcon";
import { EditIcon } from "../icons/EditIcon";
import { LockIcon } from "../icons/LockIcon";
import { ShareIcon } from "../icons/ShareIcon";
import { TrashcanIcon } from "../icons/reader/TrashcanIcon";
import { VerticalSquiggleSmallIcon } from "../icons/VerticalSquiggleSmallIcon";
import {
  List,
  ListItem,
  postDeleteList,
  postReorderListItems,
} from "../../api/lists";
import { useAccountData } from "../../hooks/useAccountData";
import { SortOrderMenu } from "../sortMenus/SortOrderMenu";
import { ConfirmAlertDialog } from "../common/ConfirmAlertDialog";
import { ReadingListDetailHeroCoverStack } from "./ReadingListDetailHeroCoverStack";
import { ReadingListDetailItemRow } from "./ReadingListDetailItemRow";
import { ReadingListEditModal } from "./ReadingListEditModal";
import { ReadingListAddComicPicker } from "./ReadingListAddComicPicker";
import { FollowListButton } from "./FollowListButton";
import { useToggleListFollow } from "./hooks/useToggleListFollow";
import { readingListDetailQueryKey } from "../../screens/shelf/ReadingListDetailScreen";

interface ReadingListDetailPaneProps {
  list: List | null;
  showBackButton: boolean;
  onBack: () => void;
}

// Pulled from the active row in the items list. We don't surface fancy
// formatters elsewhere in /lists yet, so keeping this local rather than
// importing a shared util keeps the diff small.
function formatStat(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10_000) return `${(n / 1000).toFixed(1)}K`;
  if (n < 1_000_000) return `${Math.round(n / 1000)}K`;
  if (n < 10_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  return `${Math.round(n / 1_000_000)}M`;
}

function sumItems(
  items: ListItem[],
  field: "total_pageviews" | "total_favorites" | "total_published_pages",
): number {
  let total = 0;
  for (const item of items) {
    const ctx = item.comic ?? item.release;
    const v = ctx?.[field];
    if (typeof v === "number") total += v;
  }
  return total;
}

// Item-row first-mount entrance — short translateY + fade. Stagger is applied
// per-row via animation-delay so it reads as a wave rather than a single beat.
const itemRowEnter = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

export function ReadingListDetailPane({
  list,
  showBackButton,
  onBack,
}: ReadingListDetailPaneProps) {
  const [t] = useTranslation();
  if (!list) {
    return (
      <Flex flex="1" align="center" justify="center" p="48px">
        <Text fontSize="14px" color="neutral.400">
          {t("screens.shelf.readingLists.layout.emptyDetail")}
        </Text>
      </Flex>
    );
  }
  // Inner shell does the real work — splitting the early-return out keeps
  // the edit-related hooks below from violating rules-of-hooks when there's
  // no list selected.
  return (
    <PaneContent
      list={list}
      showBackButton={showBackButton}
      onBack={onBack}
    />
  );
}

function PaneContent({
  list,
  showBackButton,
  onBack,
}: {
  list: List;
  showBackButton: boolean;
  onBack: () => void;
}) {
  const [t] = useTranslation();
  const accountData = useAccountData();
  const currentUserId = accountData?.user?.id;
  const isOwner =
    currentUserId != null &&
    list.owner_id != null &&
    list.owner_id === currentUserId;

  const navigate = useNavigate();
  const toast = useToast();

  // Confirm dialog state for the trash-icon delete flow. Lives on the pane
  // so the dialog renders outside the clipping Box (which has overflow:hidden).
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Edit-list modal state. Owner clicks "Edit list" → opens a dedicated
  // modal for title + description + visibility (mirroring the Create
  // wizard's first two steps). The hero is read-only at all times now.
  const [editOpen, setEditOpen] = useState(false);
  const deleteMutation = useMutation({
    mutationFn: () => postDeleteList(list.id),
    onSuccess: () => {
      toast({
        title: t("screens.shelf.readingLists.toasts.deleted"),
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      // Cache invalidation falls out of the navigate — the rail's
      // useReadingListsShelf re-fetches when the new URL hits.
      navigate("/my-shelf/reading-lists", { replace: true });
    },
    onError: () => {
      toast({
        title: t("screens.shelf.readingLists.toasts.deleteError"),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    },
  });

  // Follow state — local mirror of list.is_following so we can flip
  // optimistically. Resets whenever a different list loads. Non-owners only
  // (you can't follow your own list).
  const [isFollowing, setIsFollowing] = useState(list.is_following ?? false);
  useEffect(() => {
    setIsFollowing(list.is_following ?? false);
  }, [list.id, list.is_following]);
  const { toggle: toggleFollow, isPending: isFollowPending } =
    useToggleListFollow({
      listId: list.id,
      isFollowing,
      onSuccess: (next) => setIsFollowing(next),
    });

  const handleShare = async () => {
    const shareUrl =
      list.url ?? `${window.location.origin}/my-shelf/reading-lists/${list.id}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: t("screens.shelf.readingLists.detail.shareCopied"),
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch {
      toast({
        title: t("screens.shelf.readingLists.detail.shareError"),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <Flex
      direction="column"
      flex="1"
      minW={0}
      // Top breathing room separates the toolbar from the My Shelf tab
      // bar above it when the screen is mounted at /my-shelf/reading-lists/<id>.
      pt={{ base: "16px", md: "24px" }}
      // Reserve space for the fixed onboarding FooterBanner (~96px tall on
      // desktop, plus shadow + gap). Pages have to opt into clearance
      // themselves; there's no global page offset for it elsewhere.
      pb={{ base: "120px", md: "140px" }}
      position="relative"
    >
      {/* Top toolbar: back link on the left (always — Figma shows it even
          on desktop), owner action icons on the right. Pencil toggles edit
          mode (becomes a check while editing); trash and share are always
          available to the owner. */}
      <Flex
        align="center"
        justify="space-between"
        gap="12px"
        px={{ base: "8px", lg: "12px" }}
        pb="12px"
        minH="32px"
      >
        <Box>
          {showBackButton && (
            <Button
              // Ghost (no border / no fill) so the back link reads as
              // navigation, not a primary action.
              variant="ghost"
              size="sm"
              fontSize="14px"
              leftIcon={<CaretLeftIcon w="24px" h="24px" />}
              onClick={onBack}
              px="6px"
              color="dune.dune"
              _hover={{ bg: "transparent.white.05" }}
            >
              {t("screens.shelf.readingLists.layout.backToLists")}
            </Button>
          )}
        </Box>
        {isOwner && (
          <Flex align="center" gap="2px">
            <Button
              variant="tertiary"
              size="md"
              leftIcon={<EditIcon w="16px" h="16px" />}
              onClick={() => setEditOpen(true)}
            >
              {t("screens.shelf.readingLists.detail.editor.editButton")}
            </Button>
            <Tooltip
              label={t(
                "screens.shelf.readingLists.detail.editor.deleteListAria",
              )}
              openDelay={300}
            >
              <IconButton
                aria-label={t(
                  "screens.shelf.readingLists.detail.editor.deleteListAria",
                )}
                icon={<TrashcanIcon w="18px" h="18px" />}
                variant="ghost"
                size="sm"
                color="dune.dune"
                onClick={() => setDeleteOpen(true)}
                isDisabled={deleteMutation.isPending}
              />
            </Tooltip>
            <Tooltip
              label={t(
                "screens.shelf.readingLists.detail.editor.shareListAria",
              )}
              openDelay={300}
            >
              <IconButton
                aria-label={t(
                  "screens.shelf.readingLists.detail.editor.shareListAria",
                )}
                icon={<ShareIcon w="18px" h="18px" />}
                variant="ghost"
                size="sm"
                color="dune.dune"
                onClick={handleShare}
              />
            </Tooltip>
          </Flex>
        )}
        {/*
          Non-owner cluster — Follow toggle + Share. Gated on accountData
          being resolved so the button doesn't flicker on first paint
          before we know whether the viewer is the owner.
        */}
        {!isOwner && accountData != null && (
          <Flex align="center" gap="8px">
            <Tooltip
              label={t(
                "screens.shelf.readingLists.detail.editor.shareListAria",
              )}
              openDelay={300}
            >
              <IconButton
                aria-label={t(
                  "screens.shelf.readingLists.detail.editor.shareListAria",
                )}
                icon={<ShareIcon w="18px" h="18px" />}
                variant="ghost"
                size="sm"
                color="dune.dune"
                onClick={handleShare}
              />
            </Tooltip>
            <FollowListButton
              isFollowing={isFollowing}
              onClick={toggleFollow}
              isPending={isFollowPending}
            />
          </Flex>
        )}
      </Flex>
      <Box pl={{ base: "8px", lg: "12px" }} pr={{ base: "16px", lg: "40px" }}>
        <DetailHero list={list} />
        <Divider my="24px" borderColor="transparent.white.10" />
        <DetailItemsSection list={list} isOwner={isOwner} />
      </Box>
      <ConfirmAlertDialog
        isOpen={deleteOpen}
        onClose={() => {
          if (deleteMutation.isPending) return;
          setDeleteOpen(false);
        }}
        onConfirm={() => deleteMutation.mutate()}
        title={t("screens.shelf.readingLists.deleteDialog.title")}
        body={t("screens.shelf.readingLists.deleteDialog.body", {
          name: list.display_name,
        })}
        confirmLabel={t("screens.shelf.readingLists.deleteDialog.confirm")}
        isLoading={deleteMutation.isPending}
        isDestructive
      />
      <ReadingListEditModal
        list={list}
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
      />
    </Flex>
  );
}

interface DetailHeroProps {
  list: List;
}

function DetailHero({ list }: DetailHeroProps) {
  const [t] = useTranslation();
  // Measure the title column so the cover stack can scale to span its full
  // height — back-cover top aligns with title-block top, front-cover bottom
  // aligns with vitals bottom, per Figma. ResizeObserver keeps it in sync
  // when the description changes, the user types, the user toggles edit, or
  // the viewport resizes.
  const titleColRef = useRef<HTMLDivElement>(null);
  const [titleColHeight, setTitleColHeight] = useState<number | undefined>(
    undefined,
  );
  useLayoutEffect(() => {
    const el = titleColRef.current;
    if (!el) return;
    const measure = () => setTitleColHeight(el.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Flex
      direction={{ base: "column", md: "row" }}
      // Stretch on desktop so the cover column adopts the height of the
      // title column. The cover stack scales to that height and bottom-
      // anchors so the front cover lines up with the vitals row's bottom.
      align={{ base: "center", md: "stretch" }}
      gap={{ base: "16px", md: "24px" }}
      mb="32px"
    >
      <Flex
        direction="column"
        justify={{ base: "flex-start", md: "flex-end" }}
        flexShrink={0}
      >
        <ReadingListDetailHeroCoverStack
          list={list}
          // Only pass the measured height on desktop — mobile renders the
          // stack at natural size above the title column.
          height={titleColHeight}
        />
      </Flex>
      <Flex
        ref={titleColRef}
        direction="column"
        flex="1"
        minW={0}
        align={{ base: "center", md: "stretch" }}
        textAlign={{ base: "center", md: "left" }}
        gap="16px"
        pt={{ base: 0, md: "4px" }}
      >
        <Flex
          align={{ base: "center", md: "baseline" }}
          justify={{ base: "center", md: "flex-start" }}
          gap="12px"
          wrap="wrap"
        >
          <Text
            as="h1"
            fontFamily="Bricolage Grotesque"
            fontWeight={700}
            fontSize={{ base: "28px", md: "40px" }}
            lineHeight="1.1"
            letterSpacing="-0.5px"
            color="dune.dune"
            sx={{
              fontVariationSettings: '"opsz" 14, "wdth" 100',
              fontFeatureSettings: '"ss01"',
            }}
            wordBreak="break-word"
            px={{ base: "12px", md: "14px" }}
            py={{ base: "8px", md: "10px" }}
            border="1.5px solid transparent"
            borderRadius="10px"
          >
            {list.display_name}
          </Text>
          {/*
            Visibility pill — only renders when the list is private. Slots
            into the title row using the pre-existing `detail.pills` i18n
            block (originally reserved for an edit-mode visibility toggle).
            Non-clickable badge: read-only marker, the toggle itself lives
            in the edit modal. Public lists don't show a "Public" pill
            because that's the default and would just be visual noise.
          */}
          {!list.is_public && (
            <Flex
              align="center"
              gap="6px"
              px="10px"
              py="4px"
              borderRadius="999px"
              bg="rgba(255, 255, 255, 0.06)"
              border="1px solid"
              borderColor="rgba(255, 255, 255, 0.12)"
              color="neutral.300"
            >
              <LockIcon w="14px" h="14px" />
              <Text
                fontFamily="Roboto"
                fontWeight={600}
                fontSize="12px"
                lineHeight="1"
                letterSpacing="0.2px"
              >
                {t("screens.shelf.readingLists.detail.pills.private")}
              </Text>
            </Flex>
          )}
        </Flex>
        {list.description && <EditorialDescription text={list.description} />}
        {/* Stats live inside the title column — same x-origin as the
            title and description — instead of spanning the full pane,
            per Figma. */}
        <DetailStats list={list} />
      </Flex>
    </Flex>
  );
}

// View-mode description. Capped at 3 lines so the title column has a
// predictable max height — the cover stack scales to fit that envelope.
function EditorialDescription({ text }: { text: string }) {
  return (
    <Text
      fontFamily="Roboto"
      fontSize="16px"
      fontWeight={400}
      lineHeight="1.6"
      color="neutral.200"
      width="100%"
      maxW="640px"
      mx={{ base: "auto", md: 0 }}
      wordBreak="break-word"
      noOfLines={3}
      px={{ base: "12px", md: "14px" }}
      py="10px"
      border="1.5px solid transparent"
      borderRadius="10px"
    >
      {text}
    </Text>
  );
}

interface DetailStatsProps {
  list: List;
}

function DetailStats({ list }: DetailStatsProps) {
  const [t] = useTranslation();
  const items = list.list_items ?? [];
  const itemCount = list.total_items ?? items.length;
  // Prefer the backend-stamped aggregate when present — handle_get on
  // /v1/lists/<id> sums total_pageviews across items server-side with the
  // same visibility filters the per-list listing endpoints use, so the
  // numbers are consistent across surfaces. Fall back to a client-side
  // sum only when the field isn't on the wire (legacy / cached payloads).
  const totalViews = list.total_pageviews ?? sumItems(items, "total_pageviews");
  const followCount = list.follow_count ?? 0;

  // Cells map 1:1 to the Series-Detail "Vitals" row in Figma 3081:57247:
  //   views  · shelves · releases · audience
  // Audience is the max-of audience-facet across the list's items,
  // stamped server-side by handle_entity. Display labels match the
  // comic-detail surface ("All" / "13+" / "17+").
  const cells: Array<{
    key: string;
    value: string;
    label: string;
    suffix?: React.ReactNode;
  }> = [
    {
      key: "views",
      value: formatStat(totalViews),
      label: t("screens.shelf.readingLists.detail.stats.views"),
    },
    {
      key: "favorites",
      value: formatStat(followCount),
      label: t("screens.shelf.readingLists.detail.stats.favorites"),
    },
    {
      key: "series",
      value: formatStat(itemCount),
      // Label flips between "series" and "releases" based on the list type
      // so the count reads correctly for both content shapes.
      label:
        list.list_type === "releases"
          ? t("screens.shelf.readingLists.detail.stats.releases")
          : t("screens.shelf.readingLists.detail.stats.series"),
    },
    {
      key: "audience",
      value:
        list.audience_rating === "Adults"
          ? t("screens.shelf.readingLists.detail.stats.audienceAdult")
          : list.audience_rating === "Teenagers"
            ? t("screens.shelf.readingLists.detail.stats.audienceTeen")
            : t("screens.shelf.readingLists.detail.stats.audienceAll"),
      label: t("screens.shelf.readingLists.detail.stats.audience"),
    },
  ];

  return (
    <Flex
      align="stretch"
      borderRadius="12px"
      overflow="hidden"
      // Task 1: width matches the description column above (640px), so the
      // right edges line up. Centered on mobile, left-aligned on desktop.
      w="100%"
      maxW="640px"
      mx={{ base: "auto", md: 0 }}
      bg="rgba(255, 255, 255, 0.02)"
      border="1px solid"
      borderColor="rgba(255, 255, 255, 0.10)"
    >
      {cells.map((cell, i) => (
        <Flex
          key={cell.key}
          direction="column"
          align="center"
          justify="center"
          flex="1"
          minW={0}
          h="86px"
          px={{ base: "12px", md: "20px" }}
          borderLeft={i === 0 ? undefined : "1px solid"}
          borderColor="transparent.white.10"
        >
          <Flex align="center" justify="center" minW={0}>
            <Text
              as="span"
              fontFamily="Roboto"
              fontWeight={700}
              fontSize={{ base: "20px", md: "22px" }}
              lineHeight="1.2"
              color="dune.dune"
              noOfLines={1}
              wordBreak="break-word"
              flexShrink={1}
              minW={0}
            >
              {cell.value}
            </Text>
            {cell.suffix}
          </Flex>
          <Text
            mt="2px"
            fontFamily="Roboto"
            fontSize="13px"
            fontWeight={400}
            lineHeight="1.4"
            color="neutral.300"
            noOfLines={1}
            textAlign="center"
          >
            {cell.label}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
}

interface DetailItemsSectionProps {
  list: List;
  /**
   * Owner-only affordances (drag-to-reorder, trash button on rows, Add
   * comic button) only render when this is true. The pane gates this on
   * `isOwner` directly — non-owners always render the read-only layout.
   */
  isOwner: boolean;
}

type ItemSortKey = "popular" | "list_order" | "recent";

function DetailItemsSection({
  list,
  isOwner,
}: DetailItemsSectionProps) {
  const [t] = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  // Default sort is the curator's own sequence — owners reorder against
  // this sequence, so non-owners default to it too for consistency. They
  // can still pick "Most popular" or "Recently added" via the menu.
  const [sortKey, setSortKey] = useState<ItemSortKey>("list_order");
  const [pickerOpen, setPickerOpen] = useState(false);
  // Optimistic reorder state — when set, overrides the natural list_order
  // so the dragged-to position renders instantly while the mutation is in
  // flight. Cleared on success (server is now authoritative) and on error
  // (rollback to server state).
  const [localOrderedIds, setLocalOrderedIds] = useState<number[] | null>(
    null,
  );
  const items = list.list_items ?? [];

  // Owners see the canonical curator order so drag-to-reorder operates on
  // a stable sequence. Other sorts (popular / recent) don't share a mutable
  // backing field, so reorder there has no meaning.
  const effectiveSortKey: ItemSortKey = isOwner ? "list_order" : sortKey;

  // Drop the optimistic override when the underlying item set changes —
  // adding or removing items invalidates the local id sequence.
  useEffect(() => {
    setLocalOrderedIds(null);
  }, [items.length]);

  // dnd-kit sensors. PointerSensor with a small activation distance so
  // accidental clicks on the grip don't start drags. KeyboardSensor
  // gives keyboard users full reorder access.
  const dragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const reorderMutation = useMutation({
    mutationFn: (itemIds: number[]) =>
      postReorderListItems(list.id, itemIds),
    onSuccess: () => {
      // Deliberately don't invalidate after each drop. A refetch would
      // reshuffle the header cover stack (it picks the first N items by
      // index) AND replace the optimistic local order with whatever the
      // server returns mid-flight, causing rows to flicker. The local
      // optimistic state holds the order; the next item-count change
      // (add/remove) clears it via the effect above.
      queryClient.invalidateQueries({ queryKey: readingListDetailQueryKey(list.id) });
    },
    onError: () => {
      // Roll back the optimistic order; the next render reads from the
      // server-side sequence again.
      setLocalOrderedIds(null);
      toast({
        title: t("screens.shelf.readingLists.detail.editor.reorderError"),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    },
  });

  // SortOrderMenu wants ImmutableArray-typed options. The structural shape
  // is identical so we pass through with an assertion.
  const sortOptions = useMemo(
    () =>
      [
        {
          id: "popular",
          name: t("screens.shelf.readingLists.detail.sort.popular"),
        },
        {
          id: "list_order",
          name: t("screens.shelf.readingLists.detail.sort.listOrder"),
        },
        {
          id: "recent",
          name: t("screens.shelf.readingLists.detail.sort.recent"),
        },
      ] as ImmutableArray<{ id: string; name: string }>,
    [t],
  );

  const ordered = useMemo(() => {
    const withIndex = items.map((item, originalIndex) => ({
      item,
      originalIndex,
    }));
    if (effectiveSortKey === "popular") {
      // Most-viewed first, using the comic/release total_pageviews field.
      // Items missing the field sort to the bottom but keep their relative
      // original order.
      withIndex.sort((a, b) => {
        const av = (a.item.comic ?? a.item.release)?.total_pageviews ?? 0;
        const bv = (b.item.comic ?? b.item.release)?.total_pageviews ?? 0;
        if (av !== bv) return bv - av;
        return a.originalIndex - b.originalIndex;
      });
    } else if (effectiveSortKey === "recent") {
      // Backend exposes a creation timestamp on list items via the index
      // signature. Newer items first; falls back to original order when the
      // timestamp is missing on either side.
      withIndex.sort((a, b) => {
        const at = (a.item as unknown as { create_time?: number | string })
          .create_time;
        const bt = (b.item as unknown as { create_time?: number | string })
          .create_time;
        const av = typeof at === "number" ? at : Date.parse(String(at ?? ""));
        const bv = typeof bt === "number" ? bt : Date.parse(String(bt ?? ""));
        const aValid = Number.isFinite(av);
        const bValid = Number.isFinite(bv);
        if (aValid && bValid) return bv - av;
        if (aValid) return -1;
        if (bValid) return 1;
        return a.originalIndex - b.originalIndex;
      });
    } else {
      withIndex.sort((a, b) => {
        const ao = a.item.display_order;
        const bo = b.item.display_order;
        if (typeof ao === "number" && typeof bo === "number") return ao - bo;
        if (typeof ao === "number") return -1;
        if (typeof bo === "number") return 1;
        return a.originalIndex - b.originalIndex;
      });
    }
    return withIndex.map(({ item }) => item);
  }, [items, effectiveSortKey]);

  // When a reorder is in flight or just completed, override the natural
  // sort with the optimistic id sequence. Items missing from the sequence
  // (defensive — shouldn't happen) fall through unchanged.
  const displayItems: ListItem[] = useMemo(() => {
    if (localOrderedIds == null) return ordered;
    const byId = new Map<number, ListItem>();
    for (const item of items) {
      if (typeof item.id === "number") byId.set(item.id, item);
    }
    const out: ListItem[] = [];
    for (const id of localOrderedIds) {
      const it = byId.get(id);
      if (it) out.push(it);
    }
    return out;
  }, [localOrderedIds, ordered, items]);

  const isReorderEnabled = isOwner && displayItems.length > 1;

  const sortableIds = useMemo(
    () =>
      displayItems
        .map((item) => item.id)
        .filter((id): id is number => typeof id === "number"),
    [displayItems],
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const currentIds = (
      localOrderedIds ?? displayItems.map((item) => item.id)
    ).filter((id): id is number => typeof id === "number");
    const oldIndex = currentIds.indexOf(Number(active.id));
    const newIndex = currentIds.indexOf(Number(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(currentIds, oldIndex, newIndex);
    setLocalOrderedIds(next);
    reorderMutation.mutate(next);
  };

  // Power-user reorder: type a new position into the per-row "x / n" input
  // and the item teleports there. Same optimistic + silent-mutation flow as
  // drag — the user can mix the two within a single edit session.
  const handleMoveToPosition = (itemId: number, targetPosition: number) => {
    const currentIds = (
      localOrderedIds ?? displayItems.map((item) => item.id)
    ).filter((id): id is number => typeof id === "number");
    const oldIndex = currentIds.indexOf(itemId);
    if (oldIndex < 0) return;
    // Clamp the target to a real index. Users typing "1" expect top, "n"
    // expects bottom, anything outside snaps in-bounds.
    const clamped = Math.max(
      0,
      Math.min(currentIds.length - 1, targetPosition - 1),
    );
    if (clamped === oldIndex) return;
    const next = arrayMove(currentIds, oldIndex, clamped);
    setLocalOrderedIds(next);
    reorderMutation.mutate(next);
  };

  // Heading flips based on list type — "Series in list" for comics lists,
  // "Releases in list" for release lists. Matches the Figma copy.
  const headingKey =
    list.list_type === "releases"
      ? "screens.shelf.readingLists.detail.releasesInList"
      : "screens.shelf.readingLists.detail.seriesInList";

  return (
    <Box>
      <Flex
        align="center"
        justify="space-between"
        gap="12px"
        mb="16px"
        wrap="wrap"
      >
        <Flex align="center" gap="10px">
          <VerticalSquiggleSmallIcon w="6px" h="22px" color="blaze.blaze" />
          <Text
            as="h2"
            fontFamily="Bricolage Grotesque"
            fontWeight={500}
            fontSize="22px"
            lineHeight="1.2"
            color="dune.dune"
            sx={{
              fontVariationSettings: '"opsz" 14, "wdth" 100',
              fontFeatureSettings: '"ss01"',
            }}
          >
            {t(headingKey)}
          </Text>
        </Flex>
        <Flex align="center" gap="8px" wrap="wrap" justify="flex-end">
          {isOwner && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setPickerOpen(true)}
              boxShadow="0 1px 8px rgba(255, 90, 44, 0.35)"
            >
              {list.list_type === "releases"
                ? t("screens.shelf.readingLists.detail.editor.addReleaseShort")
                : t("screens.shelf.readingLists.detail.editor.addComicShort")}
            </Button>
          )}
          {/* Hide the sort menu for owners — drag-to-reorder operates on
              list_order, so showing alternative sorts would mislead. */}
          {!isOwner && ordered.length > 1 && (
            <SortOrderMenu
              sortOrder={sortKey}
              sortOrders={sortOptions}
              onSortOrderChange={(value) => setSortKey(value as ItemSortKey)}
            />
          )}
        </Flex>
      </Flex>
      {displayItems.length === 0 ? (
        <Box
          p="32px"
          borderRadius="10px"
          border="1px dashed"
          borderColor="transparent.white.10"
          bg="transparent.white.05"
        >
          <Text fontSize="13px" color="neutral.400" textAlign="center">
            {t("screens.shelf.readingLists.detail.empty")}
          </Text>
        </Box>
      ) : isReorderEnabled ? (
        <DndContext
          sensors={dragSensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortableIds}
            strategy={verticalListSortingStrategy}
          >
            <Flex direction="column" gap="8px">
              {displayItems.map((item, i) => (
                <SortableRow
                  key={item.id ?? `item-${i}`}
                  id={typeof item.id === "number" ? item.id : -i - 1}
                  enabled={typeof item.id === "number"}
                  position={i + 1}
                  total={displayItems.length}
                  onChangePosition={(target) => {
                    if (typeof item.id === "number") {
                      handleMoveToPosition(item.id, target);
                    }
                  }}
                >
                  {(handle) => (
                    <ReadingListDetailItemRow
                      item={item}
                      isOwner={isOwner}
                      listId={list.id}
                      isReleaseList={list.list_type === "releases"}
                      dragHandle={handle}
                    />
                  )}
                </SortableRow>
              ))}
            </Flex>
          </SortableContext>
        </DndContext>
      ) : (
        <Flex direction="column" gap="8px">
          {displayItems.map((item, i) => (
            <Box
              key={item.id ?? item.context_entity_id ?? `item-${i}`}
              sx={{
                animation: `${itemRowEnter} 320ms cubic-bezier(0.21, 0.6, 0.35, 1) both`,
                // Stagger reads as a wave; capped so very long lists don't
                // pause for seconds before the bottom row arrives.
                animationDelay: `${Math.min(i, 12) * 40}ms`,
              }}
            >
              <ReadingListDetailItemRow
                item={item}
                isOwner={isOwner}
                listId={list.id}
                isReleaseList={list.list_type === "releases"}
              />
            </Box>
          ))}
        </Flex>
      )}
      {isOwner && (
        <ReadingListAddComicPicker
          list={list}
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </Box>
  );
}

// Sortable shell for one item row. Provides the dnd-kit transform on the
// outer container and hands a composed drag-handle column to the consumer
// via the children render prop. The column carries the grip on top + a
// position-jump input ("x / n") below, so users can drag for short moves
// or type a new position for long jumps without leaving the keyboard.
function SortableRow({
  id,
  enabled,
  position,
  total,
  onChangePosition,
  children,
}: {
  id: number;
  enabled: boolean;
  position: number;
  total: number;
  onChangePosition: (target: number) => void;
  children: (handle: React.ReactNode) => React.ReactNode;
}) {
  const [t] = useTranslation();
  const sortable = useSortable({ id, disabled: !enabled });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    sortable;

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Lift the dragged row above its siblings + soften it a touch so its
    // shadow reads against the rest of the list.
    opacity: isDragging ? 0.85 : 1,
    zIndex: isDragging ? 10 : "auto",
    position: "relative",
    boxShadow: isDragging ? "0 12px 32px rgba(0, 0, 0, 0.45)" : undefined,
  };

  const handle = (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="4px"
      flexShrink={0}
    >
      {/* Plain Box (not IconButton) — Chakra's Button absorbs
          onPointerDown via its accessibility internals, which swallows
          dnd-kit's drag activation. */}
      <Box
        // role + tabIndex come from dnd-kit's `attributes` spread below
        // — don't pre-set them or they'll be overwritten anyway.
        aria-label={t(
          "screens.shelf.readingLists.detail.editor.reorderHandleAria",
        )}
        display="flex"
        alignItems="center"
        justifyContent="center"
        w="32px"
        h="32px"
        borderRadius="6px"
        color="dune.500"
        cursor="grab"
        // touchAction: none is required by dnd-kit for touch drag to work
        // on the activator node.
        sx={{ touchAction: "none" }}
        _hover={{ color: "dune.dune", bg: "transparent.white.05" }}
        _active={{ cursor: "grabbing", color: "blaze.blaze" }}
        // Spread dnd-kit's handlers onto the grip directly — the row body
        // stays free of drag listeners so the kebab + status pill stay
        // clickable. setActivatorNodeRef tells dnd-kit which element acts
        // as the drag activator (vs. the sortable container) so position
        // calculations stay correct when the handle is nested.
        ref={sortable.setActivatorNodeRef}
        {...attributes}
        {...listeners}
      >
        <DragGripIcon w="18px" h="18px" />
      </Box>
      <PositionInput
        value={position}
        total={total}
        onCommit={onChangePosition}
      />
    </Flex>
  );

  return (
    <Box ref={setNodeRef} style={style}>
      {children(handle)}
    </Box>
  );
}

// Tiny "x / n" cluster under the drag grip. The "x" is editable as a
// number — committing on Enter or blur teleports the row to that position
// via the same optimistic + silent-mutation pipeline that drag uses, so
// long-distance moves don't require scrolling the list.
function PositionInput({
  value,
  total,
  onCommit,
}: {
  value: number;
  total: number;
  onCommit: (target: number) => void;
}) {
  const [t] = useTranslation();
  const [draft, setDraft] = useState<string>(String(value));

  // Sync draft to the canonical position whenever the row gets reshuffled
  // by some other action (a different row's reorder, an add/remove, an
  // exit-mode refetch). Without this the input would show stale numbers.
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const trimmed = draft.trim();
    const parsed = Number.parseInt(trimmed, 10);
    if (
      !Number.isFinite(parsed) ||
      parsed === value ||
      trimmed.length === 0
    ) {
      // Empty / invalid / no-change: revert to canonical and bail.
      setDraft(String(value));
      return;
    }
    onCommit(parsed);
  };

  return (
    <Flex
      align="center"
      gap="1px"
      // Stop drag activation if the user clicks into the input. The grip's
      // listeners live on a sibling element, but dnd-kit listens on the
      // wider ancestor too — defensive.
      onPointerDown={(e) => e.stopPropagation()}
    >
      <Input
        value={draft}
        onChange={(e) => {
          // Strip non-digits while typing so the user can't put garbage
          // into the field — the commit step also clamps.
          const cleaned = e.target.value.replace(/[^0-9]/g, "");
          setDraft(cleaned);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          } else if (e.key === "Escape") {
            e.preventDefault();
            setDraft(String(value));
            (e.target as HTMLInputElement).blur();
          }
        }}
        aria-label={t(
          "screens.shelf.readingLists.detail.editor.positionInputAria",
          { position: value, total },
        )}
        variant="unstyled"
        size="xs"
        textAlign="center"
        fontFamily="Roboto"
        fontSize="11px"
        fontWeight={600}
        color="dune.dune"
        w={`${Math.max(2, String(total).length + 1)}ch`}
        h="18px"
        px="2px"
        borderRadius="4px"
        bg="transparent.white.05"
        border="1px solid"
        borderColor="transparent.white.10"
        _hover={{ borderColor: "transparent.white.20" }}
        _focus={{
          outline: "none",
          boxShadow: "none",
          borderColor: "blaze.blaze",
          bg: "transparent.white.10",
        }}
      />
      <Text
        fontFamily="Roboto"
        fontSize="11px"
        fontWeight={400}
        color="dune.500"
        lineHeight="1"
      >
        /{total}
      </Text>
    </Flex>
  );
}
